<template>
  <div>
    <CommonAlert ref="mainAlert" :default-timeout="5000"/>

    <div :class="generatorMainSectionClass">
      <div v-if="!currentGroupOrder || currentGroupOrder.removed === true">
        <div style="display: flex; justify-content: center; align-items: center; height: 100px;">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Pobieranie danych...</span>
          </div>
        </div>
      </div>
      <div v-else class="generator-table-section">
        <PackageOrderGeneratorPricingDetailsSection :key="currentGroupOrder"
                                                    :groupOrder="currentGroupOrder"
                                                    :isEmpty="isOrderEmpty"
                                                    :orderTableHeaders="pricingTableHeaders"
                                                    :wholesalers="pricingWholesalers"
                                                    v-model:selectedWholesaler="selectedWholesalerForRealisation"
                                                    @openDiscountsModal="openDiscountManager"
                                                    @onImportWholesalerPricing="handleImportWholesalerPricing"
                                                    @onExportToExcel="exportToExcel"
                                                    @onOrderSentToPricing="openSentToPricingForm"
                                                    @onSendToRealisation="handleSendToRealisation"
        />
        <div :class="generatorTableSectionClass" style="top: 224px;">
          <div v-if="isLoading" class="d-flex flex-column align-items-center justify-content-center p-2 text-muted">
            <div class="spinner-border text-primary mb-2" role="status" style="width: 2rem; height: 2rem;">
              <span class="visually-hidden">Loading...</span>
            </div>
            <span class="small fw-semibold">Pobieranie danych zamówienia...</span>
          </div>

          <div v-else ref="generatorTableView" class="scrollable-content">
            <PackageOrdersGeneratorTable
                ref="packagesOrderPricingExcelView"
                id="packagesOrderPricingExcelView"
                :data="pricingTableData"
                :headers="availableHeaders"
                :key="availableHeaders"
                @cell-click="onCellClick"
                @cell-focus="onCellFocus"
                @cell-dbclick="onCellDbClick"
                @cell-blur="onCellBlur"
                @on-context-menu="onContextMenu"
                @page-changed="onPageChanged"
                @select-row="onSelectRow"
                @mass-clear-select="onMassClearSelect"
                @un-select="onSelectRow"
                @cell-update="onCellUpdate"
                @sort="onSort"
                :isPublished="true"
                :cell-style="setCellStyle"
                :stickyHeaderTop="46"
                :stickyScrollbarBottom="0"
                :columnGroups="columnGroups"
                @column-group-action="handleWholesalerGroupAction"
            >
              <template #generator-table-header>
                <CommonTableFilterSection2
                    :filters-fetch-method="packagesModule.httpGetOrderGeneratorAvailableFilters"
                    :filters-fetch-params="{orderGroupGuid: currentOrderGroupGuid}"
                    @onSelectedFilters="updateFilters"
                    filter-main-panel-background-color="#e9ecef"
                >
                  <template #filter-additional-buttons>
                    <button
                        class="btn btn-outline-secondary btn-sm shadow-sm"
                        @click="openColumnManager"
                        title="Zarządzaj kolumnami"
                    >
                      <i class="fas fa-columns"></i>
                    </button>
                  </template>
                </CommonTableFilterSection2>
              </template>
            </PackageOrdersGeneratorTable>

            <div class="col-md-3 justify-end">
              <b-pagination
                  v-model="currentPage"
                  size="sm"
                  :total-rows="itemsTotalElements"
                  :number-of-pages="itemsTotalPages"
                  :per-page="50"
                  :first-text="currentPage != 1 ? '1' : '||'"
                  :last-text="itemsTotalPages > 1 ? itemsTotalPages.toString() : '||'"
                  style="margin-bottom: 4px;"
              />
            </div>
          </div>

          <div ref="generatorTableViewEnd"></div>
        </div>


        <!-- Discounts modal -->
        <PackageDiscountsListModal
            v-if="showDiscountsModal"
            v-model:showModal="showDiscountsModal"
            :currentPackageOrder="currentGroupOrder"
            @onPackageDiscountSave="refreshItemsData"
            :wholesaler="selectedActionWholesaler"
        />

        <!-- Column manager -->
        <TableColumnManagerModal
            v-model:showModal="showColumnManager"
            :allHeaders="pricingTableHeaders"
            :currentVisibleKeys="visibleHeaderKeys"
            @onApply="updateVisibleColumns"
        />

        <!-- Send mail modal -->
        <b-modal
            v-model="showSendMailModal"
            id="pricing-send-mail-modal"
            scrollable
            :title="mailContent.sendMailDialogTitle"
            title-class="font-18"
            size="xl"
            style="z-index:100010!important;"
            hide-footer
        >
          <b-alert
              v-model="showSendMailAlert"
              :variant="sendMailAlertType"
              class="mt-3"
              dismissible
          >{{ sendMailAlertContent }}
          </b-alert>

          <div class="form-group">
            <CommonEmailSendMessageForm
                :mailMessage="mailContent.mailMessage"
                :mailSubject="mailContent.mailSubject"
                :mailTo="mailContent.mailTo"
                :mailCc="mailContent.mailCc"
                :mailAttachmentName="mailContent.mailAttachmentName"
                @onCancelMail="showSendMailModal = false"
                @onSendMail="sendEmailWithPackageOrderData"
                :key="mailContent"
            />
          </div>
        </b-modal>
      </div>
    </div>
  </div>

</template>

<script>
import {log} from '@/state/pinia/log';
import PackageOrdersGeneratorTable from './PackageOrdersGeneratorTable.vue';
import Swal from 'sweetalert2';
import {BModal, BPagination, BAlert} from 'bootstrap-vue-next';
import {packagesHelperMethods} from '@/views/PackagesManager/helper/packagesHelperMethods';
import {usePackagesManagerModule} from '@/state/pinia/packagesManagerModule';
import {useSimplepharmAuthModule} from '@/state/pinia/simplepharmAuthModule';
import {useCommonAppModule} from '@/state/pinia/commonAppModule';
import {useLazyLoaderModule} from '@/state/pinia/lazyloaderManagerModule';
import {useLayoutStore} from '@/state/pinia';
import dateFormat from 'dateformat';
import TableColumnManagerModal from '@/components/widgets/TableColumnManagerModal.vue';
import PackageDiscountsListModal from '@/views/PackagesManager/PackageDiscountsListModal.vue';
import CommonTableFilterSection2 from '@/components/widgets/CommonTableFilterSection2.vue';
import CommonAlert from '@/components/widgets/CommonAlert.vue';
import CommonEmailSendMessageForm from '@/components/widgets/CommonEmailSendMessageForm.vue';
import {apiRequestsHelper} from '@/state/pinia/apiRequestHelper';
import PackageOrderGeneratorPricingDetailsSection
  from '@/views/PackagesManager/PackageOrderGeneratorPricingDetailsSection.vue';

export default {
  name: 'PackagesManagerOrdersGeneratorPricingList1D',
  props: {
    editOrderGroupGuid: {
      type: String,
      required: false,
    },
    displayInBootstrapRow: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {
    CommonAlert,
    CommonTableFilterSection2,
    PackageDiscountsListModal,
    TableColumnManagerModal,
    PackageOrderGeneratorPricingDetailsSection,
    BPagination,
    BModal,
    BAlert,
    CommonEmailSendMessageForm,
    PackageOrdersGeneratorTable,
  },
  computed: {
    packagesModule() {
      return usePackagesManagerModule();
    },
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonModule() {
      return useCommonAppModule();
    },
    lazyloaderModule() {
      return useLazyLoaderModule();
    },
    layoutStore() {
      return useLayoutStore();
    },
    layout: {
      get() {
        return this.layoutStore.layoutType;
      },
      set(layout) {
        this.layoutStore.changeLayoutType(layout);
      },
    },
    generatorTableSectionClass() {
      if (this.layout === 'vertical' || this.displayInBootstrapRow) {
        return 'generator-table-container-vertical windows-like-breadcrumb-card shadow-sm scrollable-container';
      } else {
        return 'generator-table-container windows-like-breadcrumb-card shadow-sm scrollable-container';
      }
    },
    generatorMainSectionClass() {
      if (this.layout === 'vertical' || this.displayInBootstrapRow) {
        return 'row package-orders-generator-site';
      } else {
        return 'package-orders-generator-site';
      }
    },
    isOrderEmpty() {
      return this.centralProductsGuids == null || this.centralProductsGuids.length === 0;
    },
    currentOrderGroupGuid() {
      if (this.currentGroupOrder && this.currentGroupOrder.groupGuid && this.currentGroupOrder.groupGuid.length > 0) {
        return this.currentGroupOrder.groupGuid;
      } else {
        return this.$route.query.orderGroupGuid;
      }
    },
    availableHeaders() {
      if (!this.visibleHeaders) {
        return [];
      }
      return this.visibleHeaders;
    },
  },
  data() {
    return {
      showSendMailModal: false,
      showSendMailAlert: false,
      sendMailAlertType: '',
      sendMailAlertContent: '',
      mailContent: {
        sendMailDialogTitle: '',
        mailMessage: '',
        mailSubject: '',
        mailTo: '',
        mailCc: '',
        mailAttachmentName: '',
      },
      currentGroupOrder: null,
      currentPage: 0,
      sortingDirection: 'ASC',
      sortingKey: 'listIndex',
      selectedFilters: [],
      itemsTotalPages: 1,
      itemsTotalElements: 0,
      pricingTableData: [],
      pricingTableHeaders: [],
      visibleHeaders: [],
      visibleHeaderKeys: [],
      packageDiscounts: [],
      pricingWholesalers: [],
      selectedWholesalers: [],
      selectedActionWholesaler: null,
      selectedWholesalerForRealisation: '',
      showDiscountsModal: false,
      showColumnManager: false,
      groupViewMode: true,
      heartbeatTimer: null,
      isLoading: false,
      orderDrugstoresCurrent: [],
      centralProductsGuids: [],
      selectedItemsGuids: [],
      currentFocusedCell: null,
      columnGroups: [],
      wholesalerPricingLookup: {},
      wholesalerPricingGroupColors: [
        {backgroundColor: '#e8f4fd', color: '#1a5276'},
        {backgroundColor: '#fef9e7', color: '#7d6608'},
        {backgroundColor: '#eafaf1', color: '#1e8449'},
        {backgroundColor: '#fde8f0', color: '#761a4e'},
        {backgroundColor: '#f0e8fd', color: '#4e1a76'},
        {backgroundColor: '#fdf0e8', color: '#764e1a'},
      ],
    };
  },
  methods: {
    openDiscountManager() {
      this.showDiscountsModal = true;
    },
    openColumnManager() {
      this.showColumnManager = true;
    },
    updateVisibleColumns(newVisibleKeys) {
      this.visibleHeaderKeys = newVisibleKeys;
      this.visibleHeaders = this.pricingTableHeaders.filter(header => this.visibleHeaderKeys.includes(header.key));
    },
    updateFilters(filters) {
      log.log(filters);
      this.selectedFilters = filters;
      this.currentPage = 0;
      this.refreshItemsData();
    },
    toggleSortingDirection() {
      this.sortingDirection = this.sortingDirection === 'ASC' ? 'DESC' : 'ASC';
    },
    onSort(event) {
      log.log('onSort event:', event);
      this.sortingKey = event.colKey;
      const header = this.availableHeaders.find(h => h.key === this.sortingKey);
      if (header && header.sortable === false) return;
      this.toggleSortingDirection();
      this.refreshItemsData();
    },
    onPageChanged(event) {
      log.log('onPageChanged event:', event);
    },
    onCellClick(event) {
      this.currentFocusedCell = event;
    },
    onCellFocus(event) {
      this.currentFocusedCell = event;
    },
    onCellDbClick(event) {
      // pricing stage: no action on double click
    },
    onCellBlur(event) {
      log.log('onCellBlur event:', event);
    },
    onContextMenu(event) {
      // no context menu in pricing view
    },
    onSelectRow(event) {
      if (event == null || event.allSelected == null) {
        this.selectedItemsGuids = [];
        return;
      }
      const currentSelectedItemGuids = [];
      for (let i = 0; i < event.allSelected.length; i++) {
        const rowProductData = this.pricingTableData[event.allSelected[i]];
        const rowProductGuid = rowProductData['groupGuid'];
        if (rowProductGuid && !currentSelectedItemGuids.includes(rowProductGuid)) {
          currentSelectedItemGuids.push(rowProductGuid);
        }
      }
      this.selectedItemsGuids = currentSelectedItemGuids;
    },
    onMassClearSelect() {
      this.selectedItemsGuids = [];
    },

    setCellValue(row, column, value) {
      this.focusCell(row, column);
      this.$refs.packagesOrderPricingExcelView.$refs.excelEditor.setCellValue(row, column, value);
    },
    setCellValueWithoutEmit(row, column, value) {
      this.focusCell(row, column);
      this.$refs.packagesOrderPricingExcelView.$refs.excelEditor.setCellValueWithoutEmit(row, column, value);
    },
    setColumnValue(column, value, filterInPayload) {
      this.$refs.packagesOrderPricingExcelView.$refs.excelEditor.setColumnValue(column, value, filterInPayload);
    },
    setColumnCalcValue(column, calcFunction, filterInPayload) {
      this.$refs.packagesOrderPricingExcelView.$refs.excelEditor.setColumnCalcValue(column, calcFunction, filterInPayload);
    },
    focusCell(row, column) {
      this.$refs.packagesOrderPricingExcelView.$refs.excelEditor.focusCell(row, column);
    },

    getSumItemDiscount(itemRecord) {
      var discountSum = 0.0;
      this.packageDiscounts.forEach((discount) => {
        if (discount.discountKey.startsWith('packageDiscount_')) {
          var discountVal = itemRecord[discount.discountKey];
          if (!discountVal) {
            discountVal = 0.0;
          }
          discountSum += parseFloat(discountVal);
        }
      });
      return discountSum.toFixed(2);
    },

    getCalculatedItemValue(itemRecord) {
      var offerPrice = 0.0;
      if (itemRecord.offerPrice != null) {
        offerPrice = parseFloat(itemRecord.offerPrice);
      }

      var itemAmount = 0;
      if (itemRecord.amount != null) {
        itemAmount = parseFloat(itemRecord.amount);
      }

      var sellingTax = 0.0;
      if (itemRecord.sellingTax != null) {
        sellingTax = parseFloat(itemRecord.sellingTax);
      }
      var calculatedDiscountPercentage = 0.0;
      if (itemRecord.calculatedDiscountPercentage != null) {
        calculatedDiscountPercentage = parseFloat(itemRecord.calculatedDiscountPercentage);
      }

      var calculatedDiscountQuota = 0.0;
      if (itemRecord.calculatedDiscountQuota != null) {
        calculatedDiscountQuota = parseFloat(itemRecord.calculatedDiscountQuota);
      }

      const offerPriceAfterDiscount = this.calculateOfferPriceAfterDiscount(offerPrice, calculatedDiscountPercentage,calculatedDiscountQuota);
      const result = {
        itemOrderedValueTaxExcl: (offerPrice * itemAmount).toFixed(2),
        itemOrderedValueTaxIncl: (offerPrice * itemAmount * (1 + sellingTax / 100)).toFixed(2),
        itemOrderedValueTaxExclAfterDiscount: (offerPriceAfterDiscount * itemAmount).toFixed(2),
        itemOrderedValueTaxInclAfterDiscount: (offerPriceAfterDiscount * itemAmount * (1 + sellingTax / 100)).toFixed(2)
      }


      return result;
    },

    calculateItemOrderedValueTaxExcl(itemRecord){
      var offerPrice = 0.0;
      if (itemRecord.offerPrice != null) {
        offerPrice = parseFloat(itemRecord.offerPrice);
      }

      var itemAmount = 0;
      if (itemRecord.amount != null) {
        itemAmount = parseFloat(itemRecord.amount);
      }

      return (offerPrice * itemAmount).toFixed(2);
    },

    calculateItemOrderedValueTaxIncl(itemRecord){
      var offerPrice = 0.0;
      if (itemRecord.offerPrice != null) {
        offerPrice = parseFloat(itemRecord.offerPrice);
      }

      var itemAmount = 0;
      if (itemRecord.amount != null) {
        itemAmount = parseFloat(itemRecord.amount);
      }

      var sellingTax = 0.0;
      if (itemRecord.sellingTax != null) {
        sellingTax = parseFloat(itemRecord.sellingTax);
      }

      return (offerPrice * itemAmount * (1 + sellingTax / 100)).toFixed(2);
    },

    calculateItemOrderedValueTaxExclAfterDiscount(itemRecord){
      var offerPrice = 0.0;
      if (itemRecord.offerPrice != null) {
        offerPrice = parseFloat(itemRecord.offerPrice);
      }

      var itemAmount = 0;
      if (itemRecord.amount != null) {
        itemAmount = parseFloat(itemRecord.amount);
      }

      var calculatedDiscountPercentage = 0.0;
      if (itemRecord.calculatedDiscountPercentage != null) {
        calculatedDiscountPercentage = parseFloat(itemRecord.calculatedDiscountPercentage);
      }

      var calculatedDiscountQuota = 0.0;
      if (itemRecord.calculatedDiscountQuota != null) {
        calculatedDiscountQuota = parseFloat(itemRecord.calculatedDiscountQuota);
      }

      const offerPriceAfterDiscount = this.calculateOfferPriceAfterDiscount(offerPrice, calculatedDiscountPercentage,calculatedDiscountQuota);
      return (offerPriceAfterDiscount * itemAmount).toFixed(2)
    },

    calculateItemOrderedValueTaxInclAfterDiscount(itemRecord){
      var offerPrice = 0.0;
      if (itemRecord.offerPrice != null) {
        offerPrice = parseFloat(itemRecord.offerPrice);
      }

      var itemAmount = 0;
      if (itemRecord.amount != null) {
        itemAmount = parseFloat(itemRecord.amount);
      }

      var sellingTax = 0.0;
      if (itemRecord.sellingTax != null) {
        sellingTax = parseFloat(itemRecord.sellingTax);
      }
      var calculatedDiscountPercentage = 0.0;
      if (itemRecord.calculatedDiscountPercentage != null) {
        calculatedDiscountPercentage = parseFloat(itemRecord.calculatedDiscountPercentage);
      }
      var calculatedDiscountQuota = 0.0;
      if (itemRecord.calculatedDiscountQuota != null) {
        calculatedDiscountQuota = parseFloat(itemRecord.calculatedDiscountQuota);
      }


      const offerPriceAfterDiscount = this.calculateOfferPriceAfterDiscount(offerPrice, calculatedDiscountPercentage,calculatedDiscountQuota);
      return (offerPriceAfterDiscount * itemAmount * (1 + sellingTax / 100)).toFixed(2);
    },

    getSumItemPercentageDiscount(itemRecord) {
      var discountSum = 0.0;
      this.packageDiscounts.forEach((discount) => {
        if (discount.discountKey.startsWith("packageDiscount_") && discount.type === "PERCENT") {
          var discountVal = itemRecord[discount.discountKey]
          if (!discountVal) {
            discountVal = 0.0;
          }
          discountSum += parseFloat(discountVal);
        }
      });


      return discountSum.toFixed(2);

    },

    getSumItemQuotaDiscount(itemRecord) {
      var discountSum = 0.0;
      this.packageDiscounts.forEach((discount) => {
        if (discount.discountKey.startsWith("packageDiscount_") && discount.type === "QUOTA") {
          var discountVal = itemRecord[discount.discountKey]
          if (!discountVal) {
            discountVal = 0.0;
          }
          discountSum += parseFloat(discountVal);
        }
      });

      return discountSum.toFixed(2);

    },

    getDiscountsSumsWithType(itemRecord,wholesalerGuid) {
      const discountsHeadersForWholesaler = this.pricingTableHeaders.filter(h => h.key.startsWith('wholesaler_' + wholesalerGuid + '_packageDiscount_'))
      var percentageDiscountSum = 0.0;
      var quotaDiscountSum = 0.0;
      console.log("Discounts headers for wholesaler:", discountsHeadersForWholesaler)
      discountsHeadersForWholesaler.forEach((discountHeader) => {
        const discountKey = discountHeader.key
        const discount = this.packageDiscounts.find(discount => discountKey.endsWith(discount.discountKey))
        if(!discount){
          console.log("Discount not found for key:", discountKey)
          return;
        }

          var discountVal = itemRecord[discountKey]
          if (!discountVal) {
            discountVal = 0.0;
          }
          console.log("Adding discounts:", discountKey)
          if(discount.type === "PERCENT"){
            percentageDiscountSum += parseFloat(discountVal);
          }else{
            quotaDiscountSum += parseFloat(discountVal);
          }

      });

      return {percentageDiscountSum: percentageDiscountSum.toFixed(2), quotaDiscountSum: quotaDiscountSum.toFixed(2)};

    },

    setCellStyle(record, item) {
      if (item && item.key === 'pickedPrice' && record && record['pickedPrice'] != null && record['pickedPrice'] !== '') {
        return {backgroundColor: '#d4edda'};
      }
      return {};
    },

    onCellUpdate(event) {
      console.log("Event to save")
      console.log(event)
      const tableRowDataObject = event.row
      const colKey = event.colKey
      const newValue = event.newVal
      const itemGroupGuid = tableRowDataObject['groupGuid'];
      const orderGroupGuid = this.currentGroupOrder.groupGuid;

      if (colKey.includes("packageDiscount_")) {

        // removing wholesaler_ prefix and _packageDiscount suffix from colKey gave us the wholesalerGuid
        const wholesalerGuid = colKey.split('_packageDiscount_')[0].split('wholesaler_')[1];

        const packageDiscount = this.packageDiscounts.find(discount => colKey === discount.discountKey);
        var discountValue = 0.0;
        if(newValue){
          discountValue = parseFloat(newValue);
        }
        console.log("Discount value:", this.packageDiscounts,packageDiscount,colKey,newValue,discountValue)
        if (packageDiscount) {
          if (packageDiscount.separateForEveryOrderItem) {
            const discountSum = this.getDiscountsSumsWithType(tableRowDataObject,wholesalerGuid);
            const percentageDiscountSum = discountSum.percentageDiscountSum;
            const quotaDiscountSum = discountSum.quotaDiscountSum;
            console.log("Discount sum:", discountSum)

            const updatePayload = {
              discountGuid: packageDiscount.guid,
              refGuid: itemGroupGuid,
              value: discountValue
            }
            // no need to handle groupViewMode because item level discount should be always the same for every item in the group
            this.packagesModule.httpUpdatePackageDiscountValue(updatePayload);
            this.updateItemOfferPriceValues(orderGroupGuid,itemGroupGuid,wholesalerGuid, percentageDiscountSum,quotaDiscountSum, tableRowDataObject.offerPrice != null ? parseFloat(tableRowDataObject.offerPrice) : 0.0);
            // waint for 500 millis to update the discounted values
            // setTimeout(() => {
            //   this.updateGroupItemsSummaryAfterDiscountColumns(itemGroupGuid);
            // }, 500);

          } else {
            const discountSum = this.getDiscountsSumsWithType(tableRowDataObject,wholesalerGuid);
            const percentageDiscountSum = discountSum.percentageDiscountSum;
            const quotaDiscountSum = discountSum.quotaDiscountSum;

            // no need to handle groupViewMode because package order level discount is applied to all items in group order
            const updatePayload = {
              discountGuid: packageDiscount.guid,
              refGuid: orderGroupGuid,
              value: newValue
            }
            this.packagesModule.httpUpdatePackageDiscountValue(updatePayload);
            // this.updateGroupItemsSummaryAfterDiscountColumns(null); // update all records summaries

            // TODO: koniecznie zamienić tą metodę na taką która updatuje rabat na wszytskich Itemach dla zadanego groupOrderGuid
            // TODO gdyz w tym przypadku zmienimay wartosc rabatu dla wszytskich pozycji w zamówieniu
            this.updateItemOfferPriceValues(orderGroupGuid,itemGroupGuid, wholesalerGuid,percentageDiscountSum,quotaDiscountSum, tableRowDataObject.offerPrice != null ? parseFloat(tableRowDataObject.offerPrice) : 0.0);
          }

        }

      } else if (colKey === 'offerPrice') {

        const wholesalerGuid = colKey.split('_offerPrice')[0].split('wholesaler_')[1];
        const discountSum = this.getDiscountsSumsWithType(tableRowDataObject,wholesalerGuid);
        const percentageDiscountSum = discountSum.percentageDiscountSum;
        const quotaDiscountSum = discountSum.quotaDiscountSum;
        const newPriceValue = newValue != null ? parseFloat(newValue) : 0.0;

        this.updateItemOfferPriceValues(orderGroupGuid,itemGroupGuid,wholesalerGuid, percentageDiscountSum,quotaDiscountSum, newPriceValue);

      }
    },

    updateGroupItemsSummaryColumns(itemGroupGuid){
      this.updateGroupItemsSummaryBeforeDiscountColumns(itemGroupGuid);
      this.updateGroupItemsSummaryAfterDiscountColumns(itemGroupGuid);
    },
    updateGroupItemsSummaryBeforeDiscountColumns(itemGroupGuid){
      const itemOrderedValueTaxExclColIndex = this.availableHeaders.findIndex(header => header.key === 'itemOrderedValueTaxExcl');
      const itemOrderedValueTaxInclColIndex = this.availableHeaders.findIndex(header => header.key === 'itemOrderedValueTaxIncl');
      this.setColumnCalcValue(itemOrderedValueTaxExclColIndex, this.calculateItemOrderedValueTaxExcl, {"groupGuid": itemGroupGuid});
      this.setColumnCalcValue(itemOrderedValueTaxInclColIndex,  this.calculateItemOrderedValueTaxIncl, {"groupGuid": itemGroupGuid});
    },
    updateGroupItemsSummaryAfterDiscountColumns(itemGroupGuid){
      var filterPayload = {};
      if(itemGroupGuid){
        filterPayload = {"groupGuid": itemGroupGuid}
      }

      const itemOrderedValueTaxExclAfterDiscountColIndex = this.availableHeaders.findIndex(header => header.key === 'itemOrderedValueTaxExclAfterDiscount');
      const itemOrderedValueTaxInclAfterDiscountColIndex = this.availableHeaders.findIndex(header => header.key === 'itemOrderedValueTaxInclAfterDiscount');
      this.setColumnCalcValue(itemOrderedValueTaxExclAfterDiscountColIndex, this.calculateItemOrderedValueTaxExclAfterDiscount, filterPayload);
      this.setColumnCalcValue(itemOrderedValueTaxInclAfterDiscountColIndex, this.calculateItemOrderedValueTaxInclAfterDiscount, filterPayload);

    },

    updateItemOfferPriceValues(orderGroupGuid, itemGroupGuid,wholesalerGuid, percentageDiscountSum, quotaDiscountSum, offerPrice){
      const colKey = this.getWholesalerPricingColumnKey(wholesalerGuid,'offerPriceAfterDiscount')
      const offerPriceAfterDiscountColIndex = this.availableHeaders.findIndex(header => header.key === colKey);
      const offerPriceAfterDiscountNewVal = this.calculateOfferPriceAfterDiscount(offerPrice, percentageDiscountSum,quotaDiscountSum);
      const payload = {
        orderGroupGuid: orderGroupGuid,
        itemGroupGuid: itemGroupGuid,
        wholesalerGuid: wholesalerGuid,
        offerPrice: offerPrice,
        offerPriceAfterDiscount: offerPriceAfterDiscountNewVal,
      }
      // no need to handle groupViewMode because item level discount should be always the same for every item in the group
      this.packagesModule.httpUpdateWholesalerPricingOfferPrice(payload,false)
      this.setColumnValue(offerPriceAfterDiscountColIndex, offerPriceAfterDiscountNewVal, {
        "groupGuid": itemGroupGuid
      });

    },

    formatDateForInput(timestamp) {
      if (!timestamp) return '';
      return dateFormat(new Date(timestamp), 'yyyy-mm-dd');
    },

    setupInitialHeadersKeysVisible(allHeaders) {
      var initiallyVisibleHeaderKeys = [];
      for (let i = 0; i < allHeaders.length; i++) {
        const header = allHeaders[i];
        if (header.key === 'ean') {
          // hidden by default
        } else {
          initiallyVisibleHeaderKeys.push(header.key);
        }
      }
      this.updateVisibleColumns(initiallyVisibleHeaderKeys);
    },

    async refreshItemsData() {
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.isLoading = true;
      log.log('Refreshing pricing data...');

      const searchAndFilterRequest = {
        fields: null,
        search: null,
        filters: this.selectedFilters,
      };

      if (this.sortingKey === 'itemOrderedValueTaxIncl' || this.sortingKey === 'itemOrderedValueTaxExcl') {
        // These columns are computed client-side and not available for server-side sorting
        return;
      }

      const payload = {
        searchAndFilterRequest: searchAndFilterRequest,
        currentPage: this.currentPage,
        sortingDirection: this.sortingDirection,
        sortingKey: this.sortingKey,
        orderGroupGuid: this.currentGroupOrder.groupGuid,
        groupViewMode: true,
        wholesalerPricingView: true,
      };

      this.pricingTableHeaders = [];
      this.pricingTableData = [];

      try {
        const response = await this.packagesModule.httpGetAllGeneratorPackageItemsByOrderGroupGuidPaging(payload)
        const items = JSON.parse(JSON.stringify(response.data.payload.content));
        console.log('items', items);
        this.itemsTotalPages = response.data.payload.totalPages || 1;
        this.itemsTotalElements = response.data.payload.totalElements || 0;
        // Load wholesaler pricing from backend and merge into rows
        const pricingResponse = await this.packagesModule.httpGetWholesalerPricingByOrderGroupGuid({
          orderGroupGuid: this.currentGroupOrder.groupGuid,
        });
        const pricingRows = pricingResponse?.data?.payload ?? [];
        const wholasalersGuids =pricingRows.map(r => r.wholesalerGuid)
        // get only distinct wholesalers guids
        const uniqueWholasalersGuids = [...new Set(wholasalersGuids)];
        const wholesalersResponse = await this.commonModule.httpGetSuppliersByGuids({guids: uniqueWholasalersGuids});
        this.selectedWholesalers = wholesalersResponse.data.payload;

        this.packageDiscounts = [];
        const fetchedPackageDiscountsResponse = await this.packagesModule.httpGetPackageDiscountsByWholesalerGuids({
          orderGroupGuid: this.currentGroupOrder.groupGuid,
          guids: uniqueWholasalersGuids,
        });
        const fetchedPackageDiscounts = fetchedPackageDiscountsResponse.data.payload;
        console.log('fetchedPackageDiscounts', fetchedPackageDiscounts);
        if (fetchedPackageDiscounts) {
          // order the fetchedPackageDiscounts by its name alphabetically
          this.packageDiscounts = fetchedPackageDiscounts.sort((a, b) => a.name.localeCompare(b.name));
        }

        // Build pricing headers
        this.pricingTableHeaders.push({key: 'listIndex', text: 'Lp.', type: 'number', width: '40px'});
        this.pricingTableHeaders.push({
          key: 'centralProductCartReference',
          text: 'CKT',
          type: 'number',
          width: '80px',
          readonly: true
        });
        this.pricingTableHeaders.push({key: 'name', text: 'Nazwa', type: 'string', width: '220px', readonly: true});
        this.pricingTableHeaders.push({key: 'bloz', text: 'Bloz', type: 'string', width: '80px', readonly: true});
        this.pricingTableHeaders.push({key: 'ean', text: 'Ean', type: 'string', width: '120px', readonly: true});
        this.pricingTableHeaders.push({key: 'amount', text: 'Ilość', type: 'number', width: '60px', readonly: true});

        // Dynamic wholesaler pricing columns (from loaded pricing data)
        this.buildWholesalerPricingHeaders();
        this.pricingTableHeaders.push({
          key: 'pickedPrice',
          text: 'Cena wybrana',
          type: 'float',
          width: '80px',
          readonly: true
        });

        this.pricingTableHeaders.push({
          key: 'calculatedDiscountPercentage',
          text: 'Suma Rabat [%]',
          type: 'float',
          width: '80px',
          readonly: true
        });
        this.pricingTableHeaders.push({
          key: 'calculatedDiscountQuota',
          text: 'Suma Rabat [Kw.]',
          type: 'float',
          width: '80px',
          readonly: true
        });

        this.pricingTableHeaders.push({
          key: 'lastPurchasePriceInDrugstoreTaxExcl',
          text: 'Ostatnia cena zakupu netto',
          type: 'float',
          width: '100px',
          readonly: true
        });
        this.pricingTableHeaders.push({key: 'sellingTax', text: 'VAT', type: 'number', width: '60px', readonly: true});
        this.pricingTableHeaders.push(
            {
              key: "itemOrderedValueTaxExcl",
              text: "Wart. zam. netto",
              type: "float",
              width: "100px",
              readonly: true,
              sortable: false,
            }
        );

        this.pricingTableHeaders.push(
            {
              key: "itemOrderedValueTaxIncl",
              text: "Wart. zam. brutto",
              type: "float",
              width: "100px",
              readonly: true,
              sortable: false,
            }
        );

        this.pricingTableHeaders.push(
            {
              key: "itemOrderedValueTaxExclAfterDiscount",
              text: "Wart. zam. netto [Rabat]",
              type: "float",
              width: "100px",
              readonly: true,
              sortable: false,
            }
        );
        this.pricingTableHeaders.push(
            {
              key: "itemOrderedValueTaxInclAfterDiscount",
              text: "Wart. zam. brutto [Rabat]",
              type: "float",
              width: "100px",
              readonly: true,
              sortable: false,
            }
        );

        this.visibleHeaders = this.pricingTableHeaders;
        this.setupInitialHeadersKeysVisible(this.pricingTableHeaders);


        this.buildWholesalerPricingLookup(pricingRows);
        this.buildWholesalerPricingHeaders();
        this.buildWholesalerColumnGroups();

        items.forEach((orderItem) => {
          const itemValues = this.getCalculatedItemValue(orderItem);
          orderItem['itemOrderedValueTaxExcl'] = itemValues.itemOrderedValueTaxExcl;
          orderItem['itemOrderedValueTaxIncl'] = itemValues.itemOrderedValueTaxIncl;
          orderItem['itemOrderedValueTaxExclAfterDiscount'] = itemValues.itemOrderedValueTaxExclAfterDiscount;
          orderItem['itemOrderedValueTaxInclAfterDiscount'] = itemValues.itemOrderedValueTaxInclAfterDiscount;
        });


        this.mergeWholesalerPricingIntoRows(items);

        this.pricingTableData = items;
        this.visibleHeaders = this.pricingTableHeaders;
        this.setupInitialHeadersKeysVisible(this.pricingTableHeaders);
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.isLoading = false;


        // Also refresh products guids for isOrderEmpty
        const centralProductsGuidsResponse = await this.packagesModule.httpGetGroupOrderProductsGuids({orderGroupGuid: this.currentGroupOrder.groupGuid});
        this.centralProductsGuids = JSON.parse(JSON.stringify(centralProductsGuidsResponse.data.payload));

        this.selectedActionWholesaler = null;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.isLoading = false;
      } catch (e) {
        this.selectedActionWholesaler = null;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.isLoading = false;
        await apiRequestsHelper.fireExceptionModal("Wystąpił błąd!", e, "error", "")
      }

    },

    handleImportWholesalerPricing(payload) {
      const {wholesalers, source} = payload;
      if (!wholesalers || wholesalers.length === 0) return;

      const importPromises = wholesalers.map((wholesaler) => {
        if (source === 'EXCEL') {
          // Placeholder for future excel rows payload integration
          const rowsFromExcel = [];
          log.log('Excel import placeholder for wholesaler:', wholesaler.guid, rowsFromExcel);
        }
        // TODO: For both OFFER and EXCEL paths, call backend add pricing set
        return this.packagesModule.httpAddWholesalerPricingSet({
          orderGroupGuid: this.currentGroupOrder.groupGuid,
          wholesalerGuid: wholesaler.guid,
        });
      });

      Promise.all(importPromises).then(() => {
        // Store the full selected wholesaler objects
        wholesalers.forEach((w) => {
          const exists = this.selectedWholesalers.some(sw => sw.guid === w.guid);
          if (!exists) {
            this.selectedWholesalers.push(w);
          }
        });

        this.refreshItemsData();
      }).catch((error) => {
        log.log('Error during wholesaler pricing import:', error);
        this.$refs.mainAlert && this.$refs.mainAlert.showAlert('danger', 'Błąd importu', 'Błąd podczas importu wyceny hurtowni.');
      });
    },

    getWholesalerPricingColumnKey(wholesalerGuid, fieldName) {
      return 'wholesaler_' + wholesalerGuid + '_' + fieldName;
    },

    buildWholesalerPricingLookup(pricingRows) {
      this.wholesalerPricingLookup = this.packagesModule.buildWholesalerPricingMap(pricingRows);
    },

    buildWholesalerPricingHeaders() {
      // Remove any existing wholesaler pricing headers
      this.pricingTableHeaders = this.pricingTableHeaders.filter(
          h => !h.key.startsWith('wholesaler_')
      );

      const wholesalerGuids = Object.keys(this.wholesalerPricingLookup);
      const insertIndex = this.pricingTableHeaders.findIndex(h => h.key === 'notifications');
      const insertAt = insertIndex >= 0 ? insertIndex : this.pricingTableHeaders.length;




      let offset = 0;
      wholesalerGuids.forEach((wholesalerGuid) => {
        const wholesaler = this.selectedWholesalers.find(w => w.guid === wholesalerGuid);
        const wholesalerName = wholesaler ? wholesaler.name : wholesalerGuid;

        this.pricingTableHeaders.splice(insertAt + offset, 0, {
          key: this.getWholesalerPricingColumnKey(wholesalerGuid, 'offerPrice'),
          text: 'Cena',
          type: 'float',
          width: '200px',
          readonly: false,
          sortable: false,
        });
        offset++;

        // Package discount columns
        this.packageDiscounts.forEach((discount) => {
          if(discount.wholesalerGuid === wholesalerGuid)
          this.pricingTableHeaders.splice(insertAt + offset, 0,{
            key: discount.discountKey,
            text: discount.name,
            type: 'float',
            width: '80px',
            readonly: false,
            sortable: false,
          });
          offset++;
        });


        this.pricingTableHeaders.splice(insertAt + offset, 0, {
          key: this.getWholesalerPricingColumnKey(wholesalerGuid, 'offerPriceAfterDiscount'),
          text: 'Cena [Rabat]',
          type: 'float',
          width: '200px',
          readonly: true,
          sortable: false,
        });
        offset++;
      });

      console.log('this.pricingTableHeaders', this.pricingTableHeaders);
    },

    buildWholesalerColumnGroups() {
      const wholesalerGuids = Object.keys(this.wholesalerPricingLookup);
      this.columnGroups = wholesalerGuids.map((wholesalerGuid, index) => {
        const wholesaler = this.selectedWholesalers.find(w => w.guid === wholesalerGuid);
        const wholesalerName = wholesaler ? wholesaler.name : wholesalerGuid;
        const colorStyle = this.wholesalerPricingGroupColors[index % this.wholesalerPricingGroupColors.length];
        // find all pricing table headers that contains phrase 'packageDiscount_' and wholesaler.guid, order it its text alphabetically and asign to new arrey
        const discountsHeadersForWholesaler = this.pricingTableHeaders.filter(h => h.key.startsWith('wholesaler_' + wholesalerGuid + '_packageDiscount_'))
            .sort((a, b) => a.text.localeCompare(b.text))

        return {
          key: 'wholesaler_' + wholesalerGuid,
          label: wholesalerName,
          order: 100 + index,
          columnKeys: [
            this.getWholesalerPricingColumnKey(wholesalerGuid, 'offerPrice'),
              ...(discountsHeadersForWholesaler.map(h => h.key)),
            this.getWholesalerPricingColumnKey(wholesalerGuid, 'offerPriceAfterDiscount'),
          ],
          actions: [
            {key: 'delete-pricing', label: 'Usuń wycenę', icon: '✕', class: 'column-group-action-btn'},
            {key: 'add-wholesaler-discount', label: 'Dodaj rabat', icon: '%', class: 'column-group-action-btn'},
          ],
          style: {
            headerStyle: {fontWeight: 'bold'},
            backgroundColor: colorStyle.backgroundColor,
            color: colorStyle.color,
          },
          insertAfterField: 'amount'

        };
      });
    },

    mergeWholesalerPricingIntoRows(items) {
      const wholesalerGuids = Object.keys(this.wholesalerPricingLookup);
      items.forEach((orderItem) => {
        const itemGroupGuid = orderItem['groupGuid'];
        wholesalerGuids.forEach((wholesalerGuid) => {
          const pricingRow = this.wholesalerPricingLookup[wholesalerGuid]?.[itemGroupGuid];
          orderItem[this.getWholesalerPricingColumnKey(wholesalerGuid, 'offerPrice')] =
              pricingRow ? pricingRow.offerPrice : 0;
          orderItem[this.getWholesalerPricingColumnKey(wholesalerGuid, 'offerPriceAfterDiscount')] =
              pricingRow ? pricingRow.offerPriceAfterDiscount : 0;
        });
      });
    },

    handleWholesalerGroupAction(payload) {
      const {groupKey, actionKey} = payload;
      const wholesalerGuid = groupKey.replace('wholesaler_', '');
      if (actionKey === 'delete-pricing'){
        // Extract wholesalerGuid from group key: wholesaler_<guid>
        this.deleteWholesalerPricingWithWarning(wholesalerGuid);
      }else if (actionKey === 'add-wholesaler-discount'){
        const selectedWholesaler = this.selectedWholesalers.find(w => w.guid === wholesalerGuid);
        if (selectedWholesaler) {
          this.selectedActionWholesaler = selectedWholesaler;
          console.log('selectedActionWholesaler', this.selectedActionWholesaler);
          this.openDiscountManager();
        }
      }



    },

    deleteWholesalerPricingWithWarning(wholesalerGuid) {
      Swal.fire({
        title: 'Usunąć wycenę?',
        text: 'Czy na pewno chcesz usunąć wycenę dla tej hurtowni?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj',
        customClass: {container: 'swal-highest'},
      }).then((result) => {
        if (result.isConfirmed) {
          this.packagesModule.httpDeleteWholesalerPricingSet({
            orderGroupGuid: this.currentGroupOrder.groupGuid,
            wholesalerGuid: wholesalerGuid,
          }).then(() => {
            // Remove from selectedWholesalers
            this.selectedWholesalers = this.selectedWholesalers.filter(w => w.guid !== wholesalerGuid);
            this.refreshItemsData();
          });
        }
      });

    },

    refreshWholesalerPricingData() {
      return this.packagesModule.httpGetWholesalerPricingByOrderGroupGuid({
        orderGroupGuid: this.currentGroupOrder.groupGuid,
      }).then((pricingResponse) => {
        const pricingRows = pricingResponse?.data?.payload ?? [];
        this.buildWholesalerPricingLookup(pricingRows);
        this.buildWholesalerPricingHeaders();
        this.buildWholesalerColumnGroups();
        this.mergeWholesalerPricingIntoRows(this.pricingTableData);
      });
    },

    openSentToPricingForm() {
      //this.exportAndSendEmailToDrugstores();TODO: CREATE EMAIL FORM ANALOGICAL TO THIS USED TO SENT ORDER TO DRUGSTORES, BU SEND TO ALL WHOLESALLERS CONSIDERED IN CURRENT PRICING PROCESS
      this.currentGroupOrder.orderStatus = 'PRICING_IN_PROGRESS';

    },


    handleSendToRealisation(payload) {
      const {wholesalerKey} = payload;
      this.packagesModule.mockSendPricingOrderToRealisation({
        orderGroupGuid: this.currentGroupOrder.groupGuid,
        wholesalerKey,
      }).then(() => {
        this.packagesModule.mockGetWholesalerEmailData({
          orderGroupGuid: this.currentGroupOrder.groupGuid,
          wholesalerKey,
        }).then((emailData) => {
          this.mailContent = {
            sendMailDialogTitle: emailData.sendMailDialogTitle || 'Wyślij zamówienie',
            mailMessage: emailData.mailMessage || '',
            mailSubject: emailData.mailSubject || '',
            mailTo: emailData.mailTo || '',
            mailCc: emailData.mailCc || '',
            mailAttachmentName: emailData.mailAttachmentName || '',
          };
          this.showSendMailAlert = false;
          this.showSendMailModal = true;
        });
      });
    },

    exportToExcel() {
      this.packagesModule.mockExportPricingToExcel({orderGroupGuid: this.currentGroupOrder.groupGuid}).then(() => {
        Swal.fire({
          title: 'Eksport zakończony',
          text: 'Plik Excel z wyceną został wygenerowany.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          customClass: {container: 'swal-highest'},
        });
      });
    },

    calculateOfferPriceAfterDiscount(offerPrice, percentageDiscountSum,quotaDiscountSum) {
      if(quotaDiscountSum == null || quotaDiscountSum === 0){
        return offerPrice - (offerPrice * percentageDiscountSum / 100);
      }else{
        return offerPrice - (offerPrice * ((percentageDiscountSum / 100.0) + (quotaDiscountSum / offerPrice)) );
      }

    },

    sendEmailWithPackageOrderData(emailData) {
      log.log(emailData);
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage('Wysyłanie wiadomości email z zamówieniem...');

      const emailPayload = {
        mailMessage: emailData,
        orderGroupGuid: this.currentGroupOrder.groupGuid,
      };

      this.packagesModule.httpSendEmailWithWithGroupOrderData(emailPayload).then((response) => {
        this.showSendMailModal = false;
        return apiRequestsHelper.handleResponseWithDialogs(response, true);
      });
    },
  },
  watch: {
    currentPage: {
      handler() {
        this.refreshItemsData();
      },
      deep: true,
    },
  },
  created() {
    this.simplepharmAuth.refreshToken().then(() => {
      this.commonModule.httpGetLicensedFeatures();
      const groupGuidFromUrlParam = this.$route.query.editOrderGroupGuid;
      const passedOrderGroupGuid = this.editOrderGroupGuid || groupGuidFromUrlParam || null;

      if (passedOrderGroupGuid != null && passedOrderGroupGuid.length > 0) {
        this.lazyloaderModule.setDisplayLazyLoader(true);
        this.lazyloaderModule.setProgressBarMessage('Pobieranie danych o zamówieniu.');
        this.packagesModule.httpGetPackageGroupOrderByGuid({orderGroupGuid: passedOrderGroupGuid}).then((response) => {
          const fetchedOrder = response.data.payload;
          const orderCreationDate = new Date(fetchedOrder.orderCreationDate)
              .toLocaleString('sv', {timeZone: 'Europe/Warsaw'})
              .split(' ')[0];
          this.currentGroupOrder = fetchedOrder;
          this.currentGroupOrder.orderCreationDate = orderCreationDate;
          this.packagesModule.httpGetDrugstoresForGroupOrder({orderGroupGuid: this.currentGroupOrder.groupGuid}).then((response) => {
            this.orderDrugstoresCurrent = response.data.payload;
          });
        }).then(() => {
          this.lazyloaderModule.setDisplayLazyLoader(false);
          this.refreshItemsData();
        });
      }
    });
  },
};
</script>

<style scoped>
.generator-table-section {
  width: 100vw;
  display: flex;
  flex-direction: column;
}

.justify-end {
  justify-content: flex-end !important;
  display: flex;
}
</style>
